/**
 * Leitura do XML da NF-e (layout 4.00, também NFC-e) para registrar compras de fornecedor.
 * Porte da regra do Tirou (features/insumos/nfe.ts), ampliado: chave de acesso, série,
 * emissão, NCM/CFOP, GTIN, totais e duplicatas.
 *
 * O que se lê e o que NÃO se lê: nada desta origem vira preço de venda. O XML alimenta o
 * CUSTO da compra, que é o número que o fornecedor tem autoridade para dizer.
 *
 * Puro: sem DOM, sem banco, sem Node. Dinheiro em centavos, convertido do texto decimal da
 * nota sem passar por ponto flutuante (19.995 × 100 não vira 1999,4999).
 */
import { PRODUCT_UNITS, type PaymentMethodTypeKey } from './settings';
import { XmlError, parseXml, xmlChild, xmlChildren, xmlFind, xmlText, type XmlElement } from './nfe-xml';

export const NFE_MAX_BYTES = 2 * 1024 * 1024;

export interface NfeAddress {
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface NfeParty {
  /** CNPJ (14, pode ser alfanumérico) ou CPF (11), só letras maiúsculas e dígitos. */
  document: string | null;
  name: string | null;
  tradeName: string | null;
  stateRegistration: string | null;
  phone: string | null;
  email: string | null;
  address: NfeAddress | null;
}

export interface NfeItem {
  /** nItem do XML (1, 2, 3...). */
  number: number;
  /** cProd: código do produto na casa do FORNECEDOR. */
  code: string | null;
  /** cEAN (ou cEANTrib) quando é um GTIN de verdade; "SEM GTIN" vira null. */
  barcode: string | null;
  description: string;
  ncm: string | null;
  cfop: string | null;
  /** uCom como veio no XML. */
  unit: string;
  /** uCom traduzida para as unidades do sistema (un, cx, pct, par, kg...). */
  unitNormalized: string;
  /** false quando a unidade não foi reconhecida e virou "un" por padrão (confira a quantidade). */
  unitRecognized: boolean;
  quantity: number;
  /** vUnCom arredondado ao centavo (o exato pode ter até 10 casas). */
  unitPriceCents: number;
  /** vProd: valor bruto exato da linha (quantidade × vUnCom). */
  grossCents: number;
  /** vDesc do ITEM (o abatimento desta linha). */
  discountCents: number;
  freightCents: number;
  insuranceCents: number;
  otherCents: number;
  /** IPI da linha (imposto/IPI/IPITrib/vIPI), quando houver. */
  ipiCents: number;
  additionalInfo: string | null;
}

export interface NfeTotals {
  productsCents: number;
  freightCents: number;
  insuranceCents: number;
  discountCents: number;
  otherCents: number;
  ipiCents: number;
  stCents: number;
  /** vNF: valor total da nota (o que se paga ao fornecedor). */
  invoiceCents: number;
}

export interface NfeInstallment {
  number: string | null;
  dueDate: string | null;
  amountCents: number;
}

export interface NfePayment {
  /** tPag (01 dinheiro, 03 crédito, 15 boleto, 17 Pix...). */
  code: string | null;
  method: PaymentMethodTypeKey | null;
  amountCents: number;
}

export interface NfeDocument {
  /** Chave de acesso (44 dígitos) quando presente e com dígito verificador correto. */
  accessKey: string | null;
  number: string | null;
  series: string | null;
  /** 55 = NF-e, 65 = NFC-e. */
  model: string | null;
  /** Instante da emissão (ISO). */
  issuedAt: string | null;
  /** Dia da emissão no fuso do emitente (AAAA-MM-DD). */
  issueDate: string | null;
  /** tpAmb = 2: nota de homologação, sem valor fiscal. */
  isTestEnvironment: boolean;
  issuer: NfeParty;
  recipient: NfeParty;
  items: NfeItem[];
  totals: NfeTotals;
  installments: NfeInstallment[];
  payments: NfePayment[];
  additionalInfo: string | null;
  /** Avisos de leitura (itens ignorados, chave inválida...), em pt-BR. */
  warnings: string[];
}

export type NfeParseError = 'invalid_xml' | 'not_nfe' | 'no_items' | 'too_large';

export type NfeParseResult = { ok: true; nfe: NfeDocument } | { ok: false; error: NfeParseError; message: string };

// ---------------------------------------------------------------------------
// Utilitários
// ---------------------------------------------------------------------------

/** Texto decimal da NF-e ("1234.56", "39.9000000000") em centavos, meio centavo para cima. */
export function decimalToCents(value: string | null | undefined): number {
  if (!value) return 0;
  const m = /^(-?)(\d+)(?:[.,](\d+))?$/.exec(value.trim());
  if (!m) return 0;
  const frac = m[3] ?? '';
  let cents = Number(m[2]) * 100 + Number((frac + '00').slice(0, 2));
  if (frac.length > 2 && frac[2]! >= '5') cents += 1;
  return m[1] ? -cents : cents;
}

function decimal(value: string | null | undefined): number {
  if (!value) return 0;
  const n = Number(value.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Dígito verificador da chave de acesso (módulo 11, pesos 2 a 9 da direita para a esquerda). */
export function isValidNfeAccessKey(key: string | null | undefined): boolean {
  if (!key || !/^\d{44}$/.test(key)) return false;
  let sum = 0;
  let weight = 2;
  for (let i = 42; i >= 0; i--) {
    sum += Number(key[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const r = sum % 11;
  const dv = r === 0 || r === 1 ? 0 : 11 - r;
  return dv === Number(key[43]);
}

/** GTIN-8/12/13/14 com dígito verificador correto. */
export function isValidGtin(code: string | null | undefined): boolean {
  if (!code || !/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(code)) return false;
  const digits = code.split('').map(Number);
  const check = digits.pop()!;
  const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

function normalizeBarcode(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  return /^\d{8,14}$/.test(v) ? v : null;
}

function normalizeDocument(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
  return v.length === 11 || v.length === 14 ? v : null;
}

/**
 * uCom é texto livre na prática: a mesma peça chega como "UN", "PC", "PÇ" ou "Peça". O que
 * não for reconhecido vira "un" com unitRecognized = false: nunca um palpite de embalagem,
 * porque errar a unidade estraga o estoque em silêncio (2 caixas entrando como 2 peças).
 */
const UNIT_SYNONYMS: Record<string, string> = {
  un: 'un', und: 'un', unid: 'un', unidade: 'un', u: 'un', pc: 'un', pca: 'un', peca: 'un', pcs: 'un', pecas: 'un',
  kit: 'un', cj: 'un', conj: 'un', conjunto: 'un', jg: 'un', jogo: 'un',
  cx: 'cx', caixa: 'cx', cxs: 'cx',
  pct: 'pct', pacote: 'pct', pac: 'pct', fd: 'pct', fardo: 'pct',
  par: 'par', pr: 'par', pares: 'par',
  kg: 'kg', quilo: 'kg', quilograma: 'kg', g: 'g', gr: 'g', grama: 'g',
  l: 'l', lt: 'l', litro: 'l', ml: 'ml', m: 'm', mt: 'm', mts: 'm', metro: 'm',
};

export function normalizeNfeUnit(raw: string | null | undefined): { unit: string; recognized: boolean } {
  if (!raw) return { unit: 'un', recognized: false };
  // NFD separa a letra do acento: "PÇ" vira "pc" (e não "p"), que casa com o dicionário.
  const clean = raw.trim().toLowerCase().normalize('NFD').replace(/[^a-z]/g, '');
  const unit = UNIT_SYNONYMS[clean];
  if (unit && PRODUCT_UNITS.some((u) => u.value === unit)) return { unit, recognized: true };
  return { unit: 'un', recognized: false };
}

/** tPag da NF-e para a forma de pagamento do sistema. */
export function nfePaymentMethod(code: string | null | undefined): PaymentMethodTypeKey | null {
  switch ((code ?? '').padStart(2, '0')) {
    case '01':
      return 'cash';
    case '03':
      return 'credit_card';
    case '04':
      return 'debit_card';
    case '05':
      return 'store_credit';
    case '10':
    case '11':
    case '12':
    case '13':
      return 'voucher';
    case '15':
      return 'boleto';
    case '16':
    case '18':
      return 'bank_transfer';
    case '17':
      return 'pix';
    case '90':
      return null;
    default:
      return code ? 'other' : null;
  }
}

function party(el: XmlElement | undefined, addressTag: string): NfeParty {
  const addr = xmlChild(el, addressTag);
  return {
    document: normalizeDocument(xmlText(el, 'CNPJ') ?? xmlText(el, 'CPF')),
    name: xmlText(el, 'xNome'),
    tradeName: xmlText(el, 'xFant'),
    stateRegistration: xmlText(el, 'IE'),
    phone: xmlText(addr, 'fone'),
    email: xmlText(el, 'email'),
    address: addr
      ? {
          street: xmlText(addr, 'xLgr'),
          number: xmlText(addr, 'nro'),
          complement: xmlText(addr, 'xCpl'),
          district: xmlText(addr, 'xBairro'),
          city: xmlText(addr, 'xMun'),
          state: xmlText(addr, 'UF'),
          zipCode: (xmlText(addr, 'CEP') ?? '').replace(/\D/g, '') || null,
        }
      : null,
  };
}

function dateKeyOf(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
}

function isoOf(value: string | null): string | null {
  if (!value) return null;
  const withTime = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00-03:00` : value;
  const t = Date.parse(withTime);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

/**
 * Lê o XML da NF-e. Devolve erro (nunca lança) quando o texto não é uma NF-e reconhecível:
 * arquivo trocado, XML de evento/cancelamento, resumo sem itens, PDF renomeado.
 */
export function parseNfeXml(xml: string): NfeParseResult {
  if (typeof xml !== 'string' || !xml.trim()) return { ok: false, error: 'invalid_xml', message: 'Envie o arquivo XML da nota fiscal.' };
  if (xml.length > NFE_MAX_BYTES) return { ok: false, error: 'too_large', message: 'O XML passa de 2 MB. Confira se é o arquivo da nota.' };
  let root: XmlElement;
  try {
    root = parseXml(xml, { maxLength: NFE_MAX_BYTES });
  } catch (err) {
    const detail = err instanceof XmlError ? ` ${err.message}` : '';
    return { ok: false, error: 'invalid_xml', message: `Não foi possível ler o arquivo como XML.${detail}` };
  }
  const inf = xmlFind(root, 'infNFe');
  if (!inf) return { ok: false, error: 'not_nfe', message: 'Este XML não é de uma NF-e. Envie o XML da nota de compra (não o de evento ou cancelamento).' };

  const warnings: string[] = [];
  const ide = xmlChild(inf, 'ide');
  const emit = xmlChild(inf, 'emit');
  const dest = xmlChild(inf, 'dest');
  const tot = xmlChild(xmlChild(inf, 'total'), 'ICMSTot');

  const items: NfeItem[] = [];
  for (const det of xmlChildren(inf, 'det')) {
    const prod = xmlChild(det, 'prod');
    const description = xmlText(prod, 'xProd');
    if (!prod || !description) continue;
    const quantity = decimal(xmlText(prod, 'qCom'));
    const number = Number(det.attrs.nItem ?? items.length + 1) || items.length + 1;
    // Item com quantidade zero existe em nota de bonificação e ajuste: entrar com ele no estoque
    // não muda saldo e ainda polui a compra.
    if (quantity <= 0) {
      warnings.push(`Item ${number} ("${description}") veio sem quantidade e foi ignorado.`);
      continue;
    }
    const unitRaw = xmlText(prod, 'uCom') ?? 'UN';
    const unit = normalizeNfeUnit(unitRaw);
    const unitPriceCents = decimalToCents(xmlText(prod, 'vUnCom'));
    const vProd = xmlText(prod, 'vProd');
    const ipi = xmlChild(xmlChild(det, 'imposto'), 'IPI');
    items.push({
      number,
      code: xmlText(prod, 'cProd'),
      barcode: normalizeBarcode(xmlText(prod, 'cEAN')) ?? normalizeBarcode(xmlText(prod, 'cEANTrib')),
      description,
      ncm: xmlText(prod, 'NCM'),
      cfop: xmlText(prod, 'CFOP'),
      unit: unitRaw,
      unitNormalized: unit.unit,
      unitRecognized: unit.recognized,
      quantity,
      unitPriceCents,
      // vProd é o BRUTO da linha por definição do layout; o desconto vem separado em vDesc.
      grossCents: vProd ? decimalToCents(vProd) : Math.round(quantity * unitPriceCents),
      discountCents: decimalToCents(xmlText(prod, 'vDesc')),
      freightCents: decimalToCents(xmlText(prod, 'vFrete')),
      insuranceCents: decimalToCents(xmlText(prod, 'vSeg')),
      otherCents: decimalToCents(xmlText(prod, 'vOutro')),
      ipiCents: decimalToCents(xmlText(xmlChild(ipi, 'IPITrib'), 'vIPI')),
      additionalInfo: xmlText(det, 'infAdProd'),
    });
  }
  if (!items.length) return { ok: false, error: 'no_items', message: 'A nota não tem itens para dar entrada.' };

  const idAttr = (inf.attrs.Id ?? '').replace(/^NFe/i, '');
  const protKey = xmlText(xmlFind(root, 'infProt'), 'chNFe');
  let accessKey: string | null = null;
  for (const candidate of [idAttr, protKey]) {
    if (candidate && /^\d{44}$/.test(candidate)) {
      if (isValidNfeAccessKey(candidate)) {
        accessKey = candidate;
        break;
      }
      warnings.push('A chave de acesso da nota tem o dígito verificador inválido e foi ignorada.');
    }
  }

  const sumItems = (pick: (i: NfeItem) => number) => items.reduce((s, i) => s + pick(i), 0);
  const totals: NfeTotals = tot
    ? {
        productsCents: decimalToCents(xmlText(tot, 'vProd')),
        freightCents: decimalToCents(xmlText(tot, 'vFrete')),
        insuranceCents: decimalToCents(xmlText(tot, 'vSeg')),
        discountCents: decimalToCents(xmlText(tot, 'vDesc')),
        otherCents: decimalToCents(xmlText(tot, 'vOutro')),
        ipiCents: decimalToCents(xmlText(tot, 'vIPI')),
        stCents: decimalToCents(xmlText(tot, 'vST')),
        invoiceCents: decimalToCents(xmlText(tot, 'vNF')),
      }
    : {
        productsCents: sumItems((i) => i.grossCents),
        freightCents: sumItems((i) => i.freightCents),
        insuranceCents: sumItems((i) => i.insuranceCents),
        discountCents: sumItems((i) => i.discountCents),
        otherCents: sumItems((i) => i.otherCents),
        ipiCents: sumItems((i) => i.ipiCents),
        stCents: 0,
        invoiceCents: sumItems((i) => i.grossCents - i.discountCents + i.freightCents + i.insuranceCents + i.otherCents + i.ipiCents),
      };
  if (!tot) warnings.push('A nota não trouxe os totais (ICMSTot): os valores foram somados pelos itens.');

  const installments: NfeInstallment[] = xmlChildren(xmlChild(inf, 'cobr'), 'dup').map((d) => ({
    number: xmlText(d, 'nDup'),
    dueDate: dateKeyOf(xmlText(d, 'dVenc')),
    amountCents: decimalToCents(xmlText(d, 'vDup')),
  }));
  const payments: NfePayment[] = xmlChildren(xmlChild(inf, 'pag'), 'detPag').map((p) => {
    const code = xmlText(p, 'tPag');
    return { code, method: nfePaymentMethod(code), amountCents: decimalToCents(xmlText(p, 'vPag')) };
  });

  const model = xmlText(ide, 'mod');
  if (model === '65') warnings.push('Este XML é de uma NFC-e (cupom fiscal ao consumidor), não de uma nota de compra.');
  const isTestEnvironment = xmlText(ide, 'tpAmb') === '2';
  if (isTestEnvironment) warnings.push('Nota emitida em ambiente de homologação (sem valor fiscal).');
  const dhEmi = xmlText(ide, 'dhEmi') ?? xmlText(ide, 'dEmi');

  return {
    ok: true,
    nfe: {
      accessKey,
      number: xmlText(ide, 'nNF'),
      series: xmlText(ide, 'serie'),
      model,
      issuedAt: isoOf(dhEmi),
      issueDate: dateKeyOf(dhEmi),
      isTestEnvironment,
      issuer: party(emit, 'enderEmit'),
      recipient: party(dest, 'enderDest'),
      items,
      totals,
      installments,
      payments,
      additionalInfo: xmlText(xmlChild(inf, 'infAdic'), 'infCpl'),
      warnings,
    },
  };
}
